import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { CREATE_QUICK_REPLY } from '~/services/conversations';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Channel {
  _id: string;
  agent_id: string;
  messaging_product: string;
  whatsapp_number: string;
  created_at: string;
}

interface Variable {
  name: string;
  position: number;
  example: string;
  component_type: string;
}

interface CreateQuickReplyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (templateId: string) => void;
  agentId?: string;
  channel?: Channel;
}

export const CreateQuickReplyDialog: React.FC<CreateQuickReplyDialogProps> = ({
  open,
  onOpenChange,
  channel,
  onSuccess,
  agentId,
}) => {
  const { t } = useTranslation(['conversations']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [templateName, setTemplateName] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [language, setLanguage] = useState('en');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [variables, setVariables] = useState<Variable[]>([]);
  const [newVariable, setNewVariable] = useState({ name: '', example: '' });

  // Control dialog visibility
  useEffect(() => {
    if (open) {
      dialogRef.current?.showModal();
    } else {
      // When closing, first reset the form
      resetForm();
      dialogRef.current?.close();
    }
  }, [open]);

  // Handle dialog closing via ESC key or clicking outside
  const handleDialogClose = () => {
    // Only notify parent if we were open
    if (open) {
      onOpenChange(false);
    }
  };

  // Custom Quill formats and modules
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet'];

  const [createQuickReply] = useMutation(CREATE_QUICK_REPLY, {
    onCompleted: (data) => {
      if (!data.create_quick_reply.hasError) {
        onOpenChange(false);
        if (onSuccess && data.create_quick_reply.data?._id) {
          onSuccess(data.create_quick_reply.data._id);
        }
      } else {
        console.error(
          data.create_quick_reply.message ||
            'Failed to create quick reply template',
        );
      }
      setIsSubmitting(false);
    },
    onError: (error) => {
      console.error('Error creating quick reply template:', error);
      setIsSubmitting(false);
    },
  });

  const resetForm = () => {
    setTemplateName('');
    setBodyText('');
    setLanguage('en');
    setFormErrors({});
    setVariables([]);
    setNewVariable({ name: '', example: '' });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const plainText = bodyText.replace(/<[^>]*>/g, '');

    if (!templateName || templateName.length < 2) {
      errors.templateName = t('template_name_min_length');
    } else if (templateName.length > 50) {
      errors.templateName = t('template_name_max_length');
    }

    if (!plainText || plainText.length < 10) {
      errors.bodyText = t('template_body_min_length');
    } else if (plainText.length > 1024) {
      errors.bodyText = t('template_body_max_length');
    }

    if (!channel?._id) {
      errors.channel = t('channel_required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Extract plain text with {{position}} variables from rich text
  const extractPlainTextWithVariables = () => {
    let text = bodyText;

    // Replace HTML with plain text
    text = text
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '\n')
      .replace(/<br\s*\/?>/g, '\n');

    // Remove other HTML tags
    text = text.replace(/<[^>]*>?/gm, '');

    // Ensure variable placeholders are formatted correctly
    variables.forEach((variable) => {
      const varPattern = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
      text = text.replace(varPattern, `{{${variable.position}}}`);
    });

    return text.trim();
  };

  const addVariable = () => {
    if (!newVariable.name || !newVariable.example) return;

    const position = variables.length + 1;
    const newVar: Variable = {
      name: newVariable.name,
      position,
      example: newVariable.example,
      component_type: 'BODY',
    };

    setVariables([...variables, newVar]);

    // Insert variable placeholder into the editor
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, `{{${newVariable.name}}}`, {
        color: '#2563eb',
        background: '#dbeafe',
        bold: true,
      });
    }

    setNewVariable({ name: '', example: '' });
  };

  const removeVariable = (index: number) => {
    const variableToRemove = variables[index];
    const updatedVariables = variables.filter((_, i) => i !== index);

    // Reorder positions
    const reorderedVariables = updatedVariables.map((v, i) => ({
      ...v,
      position: i + 1,
    }));

    setVariables(reorderedVariables);

    // Update editor content to remove the variable
    const editor = quillRef.current?.getEditor();
    if (editor && variableToRemove) {
      const content = editor.getText();
      const varPattern = `{{${variableToRemove.name}}}`;

      // Find all occurrences of the variable in the content
      const regex = new RegExp(varPattern, 'g');
      let match;
      const ranges = [];

      while ((match = regex.exec(content)) !== null) {
        ranges.push({
          index: match.index,
          length: varPattern.length,
        });
      }

      // Remove the variables from the editor content (starting from the end to avoid index issues)
      for (let i = ranges.length - 1; i >= 0; i--) {
        editor.deleteText(ranges[i].index, ranges[i].length);
      }
    }
  };

  const isFormValid = () => {
    // Check if all required fields are filled and there are no errors
    return (
      templateName.length >= 2 &&
      templateName.length <= 50 &&
      bodyText.replace(/<[^>]*>/g, '').length >= 10 &&
      bodyText.replace(/<[^>]*>/g, '').length <= 1024 &&
      !!channel?._id &&
      !!agentId
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !channel?._id || !agentId) {
      return;
    }

    const plainTextWithVariables = extractPlainTextWithVariables();

    setIsSubmitting(true);
    try {
      await createQuickReply({
        variables: {
          data: {
            template_name: templateName,
            body_text: plainTextWithVariables,
            channel_id: channel._id,
            language,
            category: 'MARKETING',
            agent_id: agentId,
            variables: variables.length > 0 ? variables : undefined,
          },
        },
      });
    } catch (error) {
      console.error('Error creating quick reply template:', error);
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const renderVariableManager = () => (
    <div className="form-control mt-4 border-t pt-4">
      <p className="text-sm text-gray-600">
        {t('template_variables_description')}
      </p>
      <label className="label">
        <span className="label-text">{t('template_variables')}</span>
      </label>

      <div className="mb-3 flex flex-wrap gap-2">
        {variables.map((variable, index) => (
          <span
            key={index}
            className="badge badge-primary gap-1 p-3 text-white"
          >
            {`{{${variable.name}}}`}
            <span className="text-xs opacity-70">({variable.example})</span>
            <button
              type="button"
              onClick={() => removeVariable(index)}
              className="btn btn-ghost btn-xs rounded-full"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          className="input-bordered input flex-1"
          placeholder={t('variable_name_example')}
          value={newVariable.name}
          onChange={(e) =>
            setNewVariable({ ...newVariable, name: e.target.value })
          }
        />
        <input
          type="text"
          className="input-bordered input flex-1"
          placeholder={t('example_value')}
          value={newVariable.example}
          onChange={(e) =>
            setNewVariable({ ...newVariable, example: e.target.value })
          }
        />
        <button
          type="button"
          className="btn btn-primary rounded-full"
          onClick={addVariable}
          disabled={!newVariable.name || !newVariable.example}
        >
          {t('add')}
        </button>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="form-control">
        <label className="label">
          <span className="label-text">{t('template_name')}</span>
        </label>
        <input
          type="text"
          className={`input-bordered input w-full ${
            formErrors.templateName ? 'input-error' : ''
          }`}
          placeholder={t('enter_template_name')}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        {formErrors.templateName && (
          <label className="label">
            <span className="label-text-alt text-error">
              {formErrors.templateName}
            </span>
          </label>
        )}
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text">{t('message_text')}</span>
        </label>
        <div
          className={`${
            formErrors.bodyText ? 'border-error rounded border' : ''
          }`}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={bodyText}
            onChange={setBodyText}
            modules={modules}
            formats={formats}
            placeholder={t('enter_message_text')}
          />
        </div>
        {formErrors.bodyText && (
          <label className="label">
            <span className="label-text-alt text-error">
              {formErrors.bodyText}
            </span>
          </label>
        )}
      </div>

      {renderVariableManager()}

      <div className="form-control">
        <label className="label">
          <span className="label-text">{t('channel')}</span>
        </label>
        <input
          type="text"
          className="input-bordered input w-full"
          value={channel?.whatsapp_number || ''}
          disabled
        />
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text">{t('language')}</span>
        </label>
        <select
          className="select-bordered select w-full"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <option value="en">{t('english')}</option>
          <option value="es">{t('spanish')}</option>
        </select>
      </div>

      <div className="mt-6 flex justify-end space-x-2">
        <button
          type="button"
          className="btn btn-primary rounded-full"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary rounded-full"
          disabled={isSubmitting || !isFormValid()}
        >
          {isSubmitting ? (
            <>
              <span className="loading loading-spinner loading-xs"></span>
              {t('creating')}
            </>
          ) : (
            t('create_template')
          )}
        </button>
      </div>
    </form>
  );

  return (
    <dialog ref={dialogRef} onClose={handleDialogClose} className="modal z-50">
      <div className="modal-box flex flex-col gap-2">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('create_quick_reply_template')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('create_quick_reply_template_description')}
          </p>
        </div>
        {renderForm()}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={handleDialogClose}>close</button>
      </form>
    </dialog>
  );
};

export default CreateQuickReplyDialog;
